import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {ReactionApiModel} from '../../../../../api/models/reaction.api.model';
import {UserApiModel} from '../../../../../api/models/user.api-model';
import {SubjectApiModel} from '../../../../../api/models/follow.api-model';
import {
  ReactedSubjectInterface,
  ReactionListItemInterface
} from '../../../../../core/interfaces/reaction-list-item.interface';
import {MediaObjectViewModel} from '../../../../../core/models/media-object.view-model';
import {SocialListHydrationService} from '../social-list-hydration.service';

@Injectable({providedIn: 'root'})
export class ReactionListItemsBuilderService {
  constructor(
    private readonly hydrationService: SocialListHydrationService,
  ) {
  }

  public buildItems$(reactionAMs: ReactionApiModel[], fallbackSubject?: SubjectApiModel): Observable<ReactionListItemInterface[]> {
    if (!reactionAMs?.length) {
      return of([]);
    }

    const authorRefs = reactionAMs.map((reactionAM) => reactionAM.author);

    return this.hydrationService.getUsersByReferences$(authorRefs).pipe(
      mergeMap((usersMap) => {
        const groupedReactionItems = reactionAMs.reduce((grouped, reactionAM) => {
          const authorId = this.hydrationService.extractResourceIri(reactionAM.author);
          const author = authorId ? usersMap.get(authorId) : null;
          if (!author?.['@id'] || author.deleted) {
            return grouped;
          }

          const reacted = (fallbackSubject || this.normalizeSubject(reactionAM.subject)) as SubjectApiModel;
          if (!reacted?.['@id']) {
            return grouped;
          }

          const reactedSubject: ReactedSubjectInterface = {
            '@id': reacted['@id'],
            apiModel: reacted,
            avatar: null,
            slug: reacted.slug,
            identity: reacted.identity
          };

          const existing = grouped.find((item) => item.author['@id'] === author['@id']);
          if (existing) {
            existing.ids.push(reactionAM['@id']);
            existing.reactedTo.push(reactedSubject);
            existing.reactedWith.push(reactionAM.reactedWith);
          } else {
            grouped.push({
              ids: [reactionAM['@id']],
              author: {
                '@id': author['@id'],
                apiModel: author,
                avatar: null,
                slug: author.slug,
                username: author.username
              },
              reactedTo: [reactedSubject],
              reactedWith: [reactionAM.reactedWith],
              resource: 'reactions'
            });
          }

          return grouped;
        }, [] as ReactionListItemInterface[]);

        const mediaRefs = [
          ...Array.from(usersMap.values()).map((userAM: UserApiModel) => userAM.avatar),
          ...groupedReactionItems.flatMap((item) => item.reactedTo.map((reacted) => reacted.apiModel.avatar))
        ];

        return this.hydrationService.getMediaMap$(mediaRefs).pipe(
          map((mediaMap) => groupedReactionItems.map((item) => this.hydrateReactionItem(item, mediaMap)))
        );
      }),
      catchError(() => of([]))
    );
  }

  private normalizeSubject(subject: string | SubjectApiModel | null | undefined): SubjectApiModel | null {
    if (!subject) {
      return null;
    }

    if (typeof subject === 'string') {
      return {'@id': subject} as SubjectApiModel;
    }

    return subject;
  }

  public mergeItems(existingItems: ReactionListItemInterface[], incomingItems: ReactionListItemInterface[]): ReactionListItemInterface[] {
    const mergedByAuthor = new Map<string, ReactionListItemInterface>();

    [...existingItems, ...incomingItems].forEach((item) => {
      const authorId = item?.author?.['@id'];
      if (!authorId) {
        return;
      }

      const existing = mergedByAuthor.get(authorId);
      if (!existing) {
        mergedByAuthor.set(authorId, {
          ...item,
          ids: [...item.ids],
          reactedTo: [...item.reactedTo],
          reactedWith: [...item.reactedWith]
        });
        return;
      }

      const existingIds = new Set(existing.ids);
      item.ids.forEach((id, index) => {
        if (existingIds.has(id)) {
          return;
        }

        existing.ids.push(id);
        existing.reactedTo.push(item.reactedTo[index]);
        existing.reactedWith.push(item.reactedWith[index]);
        existingIds.add(id);
      });
    });

    return Array.from(mergedByAuthor.values());
  }

  private hydrateReactionItem(
    item: ReactionListItemInterface,
    mediaMap: Map<string, MediaObjectViewModel>
  ): ReactionListItemInterface {
    const authorAvatarId = this.hydrationService.extractResourceIri(item.author.apiModel.avatar);
    item.author.avatar = authorAvatarId ? mediaMap.get(authorAvatarId) || null : null;

    item.reactedTo.forEach((reacted) => {
      const reactedAvatarId = this.hydrationService.extractResourceIri(reacted.apiModel.avatar);
      reacted.avatar = reactedAvatarId ? mediaMap.get(reactedAvatarId) || null : null;
    });

    return item;
  }
}
